import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, style, transition, animate } from '@angular/animations';
import { SnapshotService } from '../../shared/services/snapshot/snapshot.service';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { Snapshot } from '../../shared/interfaces/snapshot.interface';
import { ToastrService } from 'ngx-toastr';
import { AutoUnsubscribe } from '../../shared/decorators/auto-unsubscribe.decorator';
import { Subscription } from 'rxjs';
import { MessageService } from '../../shared/services/message/message.service';
import { RELOAD_SNAPSHOTS } from '../../shared/constants/general.constants';
import { toValidDate } from '../../shared/helpers/date-formatting.helper';
import { CreateSnapshotModalData } from '../create-snapshot-modal/create-snapshot-modal.component';
import { EditSnapshotModalData } from '../edit-snapshot-modal/edit-snapshot-modal.component';
import { ViewSnapshotDescriptionModalData } from '../view-snapshot-description-modal/view-snapshot-description-modal.component';
import { ConfirmApplySnapshotDialogData } from '../confirm-apply-snapshot-dialog/confirm-apply-snapshot-dialog.component';
import { SaveAndApplySnapshotDialogData } from '../save-and-apply-snapshot-dialog/save-and-apply-snapshot-dialog.component';
import { UnsavedChangesService } from '../../shared/services/unsaved-changes/unsaved-changes.service';
import { CacheService } from '../../shared/services/cache/cache.service';
import { NotebookVariablesService } from '../../shared/services/notebook-variables/notebook-variables.service';

export interface SnapshotModalData {
  projectId: number;
  versionId: number;
  workflowId?: number;
  currentSnapshotId?: number;
}

export interface SnapshotModalResult {
  action: 'apply' | 'cancel' | 'delete';
  selectedSnapshot?: Snapshot | null;
  selectedSnapshots?: Snapshot[];
}

/**
 * Snapshot management constants
 */
const SNAPSHOT_CONFIG = {
  DESCRIPTION_PREVIEW_LENGTH: 60,
  SEARCH_FOCUS_DELAY_MS: 250
} as const;

@AutoUnsubscribe()
@Component({
  selector: 'app-snapshot-management-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatMenuModule,
    MatTooltipModule
  ],
  templateUrl: './snapshot-management-modal.component.html',
  styleUrls: ['./snapshot-management-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class SnapshotManagementModalComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<SnapshotManagementModalComponent>>(MatDialogRef);
  data = inject<SnapshotModalData>(MAT_DIALOG_DATA);
  private readonly snapshotService = inject(SnapshotService);
  private toastrService = inject(ToastrService);
  private dialogService = inject(DialogService);
  private unsavedChangesService = inject(UnsavedChangesService);
  private cacheService = inject(CacheService);
  private notebookVariablesService = inject(NotebookVariablesService);
  private messageService = inject(MessageService);

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  snapshots = signal<Snapshot[]>([]);
  filteredSnapshots = signal<Snapshot[]>([]);
  selectedSnapshot = signal<Snapshot | null>(null);
  currentMenuSnapshot: Snapshot | null = null;
  searchQuery: string = '';
  loading = signal(false);
  isSearchOpen: boolean = false;

  private subscriptions: Subscription[] = [];

  ngOnInit() {
    this.loadSnapshots();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadSnapshots() {
    this.loading.set(true);
    const subscription = this.snapshotService.getSnapshots().subscribe({
      next: (snapshots: Snapshot[]) => {
        this.snapshots.set(snapshots);
        this.filteredSnapshots.set([...snapshots]);
        this.setInitialSelection();
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading snapshots:', error);
        this.toastrService.error('Failed to load snapshots');
        this.loading.set(false);
      }
    });
    this.subscriptions.push(subscription);
  }

  private setInitialSelection() {
    if (this.data.currentSnapshotId) {
      const currentSnapshot = this.snapshots().find(s => s.snapshotId === this.data.currentSnapshotId);
      if (currentSnapshot) {
        this.selectedSnapshot.set(currentSnapshot);
      }
    }
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredSnapshots.set([...this.snapshots()]);
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredSnapshots.set(this.snapshots().filter(snapshot =>
      snapshot.name.toLowerCase().includes(query) ||
      snapshot.userName?.toLowerCase().includes(query) ||
      (toValidDate(snapshot.createdAt)?.toLocaleDateString() ?? '').includes(query)
    ));
  }

  onSnapshotSelect(snapshot: Snapshot) {
    this.selectedSnapshot.set(snapshot);
  }

  setCurrentSnapshot(snapshot: Snapshot) {
    this.currentMenuSnapshot = snapshot;
  }

  getDescriptionPreview(content: string): string {
    if (!content || !content.trim()) {
      return '';
    }
    // Return first N characters with ellipsis if longer
    const trimmed = content.trim();
    const maxLength = SNAPSHOT_CONFIG.DESCRIPTION_PREVIEW_LENGTH;
    return trimmed.length > maxLength ? trimmed.substring(0, maxLength) + '...' : trimmed;
  }

  openDescriptionModal(snapshot: Snapshot) {
    const modalData: ViewSnapshotDescriptionModalData = {
      snapshot: snapshot
    };

    this.dialogService.viewSnapshotDescriptionModal(modalData).subscribe(() => {
      // Handle modal close if needed
    });
  }

  onAddNewSnapshot() {
    const modalData: CreateSnapshotModalData = {
      projectId: this.data.projectId,
      versionId: this.data.versionId,
      workflowId: this.data.workflowId
    };

    this.dialogService.createSnapshotModal(modalData).subscribe((result: any) => {
      if (result && result.action === 'create') {
        this.loadSnapshots(); // Refresh the snapshot list
        // Notify other components (like sidebar snapshot list) to refresh
        this.messageService.sendMessage(RELOAD_SNAPSHOTS);
      }
    });
  }

  isSnapshotSelected(snapshot: Snapshot): boolean {
    return this.selectedSnapshot()?.snapshotId === snapshot.snapshotId;
  }

  isSnapshotCurrent(snapshot: Snapshot): boolean {
    return snapshot.snapshotId === this.data.currentSnapshotId;
  }

  onApplyDefault() {
    this.dialogRef.close({
      action: 'apply',
      selectedSnapshot: null // Apply default (no snapshot)
    } as SnapshotModalResult);
  }

  onApplySelected() {
    if (!this.selectedSnapshot()) {
      return;
    }

    const hasUnsavedChanges = this.unsavedChangesService.hasUnsavedChanges();

    if (!hasUnsavedChanges) {
      // No unsaved changes, apply directly
      this.applySelectedSnapshot();
      return;
    }

    // Show first confirmation modal
    this.showApplyConfirmationDialog();
  }

  private showApplyConfirmationDialog() {
    if (!this.selectedSnapshot()) {
      return;
    }

    const confirmData: ConfirmApplySnapshotDialogData = {
      snapshotName: this.selectedSnapshot()!.name
    };

    this.dialogService.confirmApplySnapshotDialog(confirmData).subscribe((result: any) => {
      if (!result) {
        return;
      }

      switch (result.action) {
        case 'apply_without_saving':
          this.applySelectedSnapshot();
          break;
        case 'save_and_apply':
          this.showSaveAndApplyDialog();
          break;
        case 'cancel':
          // Do nothing, just close the dialog
          break;
      }
    });
  }

  private showSaveAndApplyDialog() {
    if (!this.selectedSnapshot()) {
      return;
    }

    const saveData: SaveAndApplySnapshotDialogData = {
      snapshotName: this.selectedSnapshot()!.name
    };

    this.dialogService.saveAndApplySnapshotDialog(saveData).subscribe((result: any) => {
      if (!result || result.action !== 'save_and_apply') {
        return;
      }

      // Save current state as new snapshot, then apply selected
      this.saveCurrentStateAndApplySelected(result.newSnapshotName!, result.newSnapshotComment);
    });
  }

  private saveCurrentStateAndApplySelected(newSnapshotName: string, newSnapshotComment?: string) {
    // Get current cache to create snapshot from
    this.cacheService.get().subscribe({
      next: (cache) => {
        if (!cache) {
          this.toastrService.error('Failed to get current cache state');
          return;
        }

        // Create snapshot data
        const snapshotData = {
          name: newSnapshotName,
          cacheId: cache.cacheId,
          content: Array.isArray(this.notebookVariablesService.variableList) &&
            this.notebookVariablesService.variableList.length > 0
            ? JSON.stringify(this.notebookVariablesService.variableList)
            : newSnapshotComment || ''
        };

        // Create the snapshot
        this.snapshotService.createSnapshot(snapshotData).subscribe({
          next: () => {
            this.toastrService.success('Current state saved as snapshot');
            // Now apply the selected snapshot
            this.applySelectedSnapshot();
          },
          error: (error) => {
            console.error('Error creating snapshot:', error);
            this.toastrService.error('Failed to save current state');
          }
        });
      },
      error: (error) => {
        console.error('Error getting cache:', error);
        this.toastrService.error('Failed to get current state');
      }
    });
  }

  private applySelectedSnapshot() {
    if (this.selectedSnapshot()) {
      this.dialogRef.close({
        action: 'apply',
        selectedSnapshot: this.selectedSnapshot()
      } as SnapshotModalResult);
    }
  }

  onDeleteSnapshotById(snapshotId: number) {
    const snapshot = this.snapshots().find(s => s.snapshotId === snapshotId);
    if (!snapshot) {
      return;
    }

    this.dialogRef.close({
      action: 'delete',
      selectedSnapshots: [snapshot]
    } as SnapshotModalResult);
  }

  onEditSnapshot(snapshot: Snapshot, event: Event) {
    event.stopPropagation();

    const modalData: EditSnapshotModalData = {
      snapshot: snapshot
    };

    this.dialogService.editSnapshotModal(modalData).subscribe((result: any) => {
      if (result && result.action === 'save') {
        this.loadSnapshots(); // Refresh the snapshot list
        // Notify other components (like sidebar snapshot list) to refresh
        this.messageService.sendMessage(RELOAD_SNAPSHOTS);
      }
    });
  }

  onCancel() {
    this.dialogRef.close({
      action: 'cancel'
    } as SnapshotModalResult);
  }

  toggleSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;

    if (this.isSearchOpen) {
      // Focus the search input after the animation completes
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, SNAPSHOT_CONFIG.SEARCH_FOCUS_DELAY_MS);
    } else {
      // Clear search when closing
      this.clearSearch();
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onSearch();
  }
}
